// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Column definitions for the federal bills browser table.
 */
import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BookOpen, CheckCircle2, ExternalLink, FileText, Loader2, MoreVertical, XCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { BillTextVersion, FederalBillMetadata } from '../models/types'
import { formatDate } from '../utils/formatters'
import { govinfoUrl } from '../utils/govinfo'
import type { AgentRunOption } from './useMarylandBillsColumns'

/** Map bill type codes to congress.gov URL slugs. */
const BILL_TYPE_SLUGS: Record<string, string> = {
  hr: 'house-bill',
  s: 'senate-bill',
  hjres: 'house-joint-resolution',
  sjres: 'senate-joint-resolution',
  hconres: 'house-concurrent-resolution',
  sconres: 'senate-concurrent-resolution',
  hres: 'house-resolution',
  sres: 'senate-resolution',
}

function congressGovUrl(bill: FederalBillMetadata): string {
  const slug = BILL_TYPE_SLUGS[bill.type.toLowerCase()] ?? bill.type.toLowerCase()
  return `https://www.congress.gov/bill/${bill.congress}th-congress/${slug}/${bill.number}`
}

/** Get the selected (or latest) version for a bill. */
function getSelectedVersion(
  bill: FederalBillMetadata,
  selectedVersions: Map<string, number>,
): BillTextVersion | undefined {
  const versions = bill.textVersions
  if (!versions || versions.length === 0) return undefined
  const key = `${bill.type}-${bill.number}`
  const selectedNum = selectedVersions.get(key)
  if (selectedNum) return versions.find(v => v.versionNumber === selectedNum)
  return versions[versions.length - 1] // latest (chronological order)
}

export interface UseFederalBillsColumnsParams {
  selectedVersions: Map<string, number>
  onVersionChange: (bill: FederalBillMetadata, versionNumber: number) => void
  loadingPdfBill: string | null
  agents?: AgentRunOption[]
  onRunAgent?: (bill: FederalBillMetadata, pipelineId: string) => void
  reviewStatuses?: Map<string, string>
  congress?: number | null
  summaryKeys?: Set<string>
  onViewSummary?: (bill: FederalBillMetadata) => void
}

export function useFederalBillsColumns({ selectedVersions, onVersionChange, loadingPdfBill, agents, onRunAgent, reviewStatuses, congress, summaryKeys, onViewSummary }: UseFederalBillsColumnsParams): ColumnDef<FederalBillMetadata, unknown>[] {
  return useMemo(
    () => [
      {
        id: 'billNumber',
        header: 'Bill',
        accessorFn: (row) => `${row.type.toUpperCase()}-${row.number}`,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <div className="max-w-[200px] lg:max-w-xs xl:max-w-sm">
                <div className="font-medium line-clamp-2">{row.original.title}</div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm">
              {row.original.title}
            </TooltipContent>
          </Tooltip>
        ),
      },
      {
        id: 'latestAction',
        header: 'Latest Action',
        meta: { className: 'hidden lg:table-cell' },
        accessorFn: (row) => row.latestAction?.actionDate ?? '',
        cell: ({ row }) => {
          const action = row.original.latestAction
          if (!action) return <span className="text-sm text-muted-foreground">-</span>
          return (
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <div className="text-sm whitespace-nowrap">{formatDate(action.actionDate)}</div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-sm">
                {action.text}
              </TooltipContent>
            </Tooltip>
          )
        },
      },
      {
        id: 'version',
        header: 'Version',
        cell: ({ row }) => {
          const bill = row.original
          const versions = bill.textVersions
          if (!versions || versions.length === 0) {
            return <span className="text-sm text-muted-foreground">-</span>
          }
          const current = getSelectedVersion(bill, selectedVersions)
          return (
            <div className="flex items-center gap-2">
              <Select
                value={String(current?.versionNumber)}
                onValueChange={(v) => onVersionChange(bill, Number(v))}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((v) => (
                    <SelectItem key={v.versionNumber} value={String(v.versionNumber)}>
                      v{v.versionNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {current && (
                <div className="hidden md:block text-xs text-muted-foreground leading-tight">
                  <div>{current.type}</div>
                  {current.date && <div>{formatDate(current.date)}</div>}
                </div>
              )}
            </div>
          )
        },
      },
      // Score and analysis columns are injected by useRendererColumns (see FederalBillsBrowser)
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const bill = row.original
          const billKey = `${bill.type}-${bill.number}`
          const billTestId = `lr-federal-${bill.type.toLowerCase()}-${bill.number}`
          const isLoading = loadingPdfBill === billKey
          const reviewStatus = reviewStatuses?.get(billKey)
          const version = getSelectedVersion(bill, selectedVersions)
          const isRunning = reviewStatus === 'running'
          return (
          <div className="flex items-center gap-1">
            <span className="sr-only" data-testid={`${billTestId}-status`}>
              {reviewStatus ?? 'idle'}
            </span>
            {agents && agents.length > 0 && onRunAgent && (
              <>
                {reviewStatus === 'completed' && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" data-testid={`${billTestId}-status-completed`} />
                )}
                {reviewStatus === 'error' && (
                  <XCircle className="h-4 w-4 text-destructive" data-testid={`${billTestId}-status-error`} />
                )}
                {isRunning ? (
                  <Loader2 className="h-4 w-4 animate-spin" data-testid={`${billTestId}-status-running`} />
                ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`${billTestId}-actions`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {agents.map((a) => (
                        <DropdownMenuItem
                          key={a.agentId}
                          onClick={() => onRunAgent(bill, a.pipelineId)}
                          data-testid={`${billTestId}-run-${a.agentId}`}
                        >
                          Run &ldquo;{a.label}&rdquo;
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
            {summaryKeys && summaryKeys.has(`${bill.type.toLowerCase()}-${bill.number}`) && onViewSummary && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewSummary(bill)}
                title="View Summary"
                data-testid={`${billTestId}-summary`}
              >
                <BookOpen className="h-4 w-4" />
              </Button>
            )}
            {version && (
              <Button
                variant="ghost"
                size="sm"
                disabled={isLoading}
                title="View PDF"
                asChild
              >
                <a
                  data-testid={`${billTestId}-pdf`}
                  href={govinfoUrl(bill.congress, bill.type, bill.number, version.suffix, 'pdf')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {isLoading
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <FileText className="h-4 w-4" />}
                </a>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              asChild
            >
              <a
                data-testid={`${billTestId}-external`}
                href={congressGovUrl(bill)}
                target="_blank"
                rel="noopener noreferrer"
                title="View on Congress.gov"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
          )
        },
      },
    ],
    [selectedVersions, onVersionChange, loadingPdfBill, agents, onRunAgent, reviewStatuses, congress, summaryKeys, onViewSummary],
  )
}
